import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Video, Image as ImageIcon, Sparkles, Send, Play, Radio, CheckSquare, Calendar, RefreshCw, BarChart2, Plus, Trash2, Heart, MessageSquare, Megaphone, Check } from 'lucide-react';
import { SocialPost, NexusUser } from '../types/nexus';

interface NexusMediaAdSocialProps {
  user: NexusUser;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onDeductRenders: (amount: number) => void;
  posts: SocialPost[];
  onPostsChanged: (newPosts: SocialPost[]) => void;
}

export const NexusMediaAdSocial: React.FC<NexusMediaAdSocialProps> = ({
  user,
  onAddLog,
  onDeductRenders,
  posts,
  onPostsChanged
}) => {
  const [activeSubView, setActiveSubView] = useState<'media' | 'ad' | 'social'>('media');

  // Media generation state
  const [mediaPrompt, setMediaPrompt] = useState('');
  const [renderType, setRenderType] = useState<'Image' | 'Video'>('Image');
  const [rendersList, setRendersList] = useState<Array<{ id: string, prompt: string, type: string, status: 'Compiling' | 'Ready', url?: string }>>([
    { id: 'rnd-1', prompt: 'Cosmic holographic landing dashboard high contrast', type: 'Image', status: 'Ready', url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=400' }
  ]);
  const [isRendering, setIsRendering] = useState(false);

  // Ad scripts state
  const [adSubject, setAdSubject] = useState('Nexus OS platform launch');
  const [generatedScript, setGeneratedScript] = useState<any | null>(null);
  const [isGeneratingAd, setIsGeneratingAd] = useState(false);

  // Social automation state
  const [newPostCaption, setNewPostCaption] = useState('');
  const [newPostPlatform, setNewPostPlatform] = useState<'LinkedIn' | 'Twitter' | 'YouTube' | 'Facebook'>('LinkedIn');

  const handleGenerateMedia = (e: React.FormEvent) => {
    e.preventDefault();
    if (!mediaPrompt.trim()) return;

    if (user.rendersRemaining <= 0) {
      onAddLog('Media Studio', `Render Prompt: "${mediaPrompt}"`, 'DENIED', 'Insufficent render credits.');
      alert('Zero render credits remaining! Buy some more packs in the Marketplace.');
      return;
    }

    setIsRendering(true);
    onAddLog('Media Studio', `Render ${renderType}: "${mediaPrompt}"`, 'SUCCESS', 'Render dispatched to GPU cluster.');

    setTimeout(() => {
      setIsRendering(false);
      onDeductRenders(1);

      const generatedUrl = renderType === 'Image'
        ? 'https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?auto=format&fit=crop&q=80&w=400'
        : undefined;

      const newRender = {
        id: `rnd-${Date.now()}`,
        prompt: mediaPrompt,
        type: renderType,
        status: 'Ready' as const,
        url: generatedUrl
      };

      setRendersList([newRender, ...rendersList]);
      setMediaPrompt('');
      onAddLog('Media Studio', 'Render complete', 'SUCCESS', 'Generation success. Compressed output matched hash.');
      alert('Visual render built successfully!');
    }, 2000);
  };

  const handleGenerateAdScript = () => {
    setIsGeneratingAd(true);
    onAddLog('Ad Factory', `Generate script: "${adSubject}"`, 'SUCCESS', 'Ad script compilation initiated.');

    setTimeout(() => {
      setIsGeneratingAd(false);
      const scripts = {
        hook: `HOOK (Visual: Zooming into a glowing purple holographic dashboard...): "Every business operates on separate sheets, lost logins, and broken tools. What if one operating system ran search, hosting, content generation, and autonomous agents under ONE single login keys?!"`,
        body: `BODY INTRO: "Meet Nexus OS. The multi-tenant dashboard that handles competitor telemetry searches for 1p, spins up bare-metal websites on Nginx containers in seconds, and deploys high-speed marketing bots to do your outreach automatically."`,
        cta: `CALL TO ACTION: "Cirvument standard admin queues. Start your 60-second preview trial right now on Nexus OS and scale your yield metrics on autopilot."`
      };
      setGeneratedScript(scripts);
      onAddLog('Ad Factory', 'Ad script compilation success', 'SUCCESS', 'Successfully returned compiled Ad & UGC hooks script.');
    }, 1500);
  };

  const handleCreateSocialPost = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPostCaption.trim()) return;

    const newPost: SocialPost = {
      id: `post-${Date.now()}`,
      platform: newPostPlatform,
      caption: newPostCaption,
      status: 'Queued',
      scheduledTime: 'In 3 hours'
    };

    onPostsChanged([newPost, ...posts]);
    setNewPostCaption('');
    onAddLog('Social Automator', `Schedule post to ${newPostPlatform}`, 'SUCCESS', 'Queued post into publishing scheduler.');
    alert('Social media post queued successfully!');
  };

  const handleApprovePost = (id: string) => {
    const updated = posts.map(p => p.id === id ? { ...p, status: 'Approved' as const } : p);
    onPostsChanged(updated);
    onAddLog('Social Automator', 'Approve queued post', 'SUCCESS', 'Director approved content draft.');
  };

  const handleDeletePost = (id: string) => {
    const updated = posts.filter(p => p.id !== id);
    onPostsChanged(updated);
    onAddLog('Social Automator', 'Remove post from scheduler', 'SUCCESS', 'Post removed from publish matrix.');
  };

  return (
    <div className="w-full space-y-6">
      {/* Sub-view Header Navigation tabs */}
      <div className="flex flex-wrap gap-2 bg-black/60 p-2 border border-zinc-850 rounded-2xl max-w-lg">
        <button
          onClick={() => setActiveSubView('media')}
          className={`flex-1 py-2 px-3 rounded-xl font-mono text-[10px] font-black uppercase text-center transition-all cursor-pointer ${
            activeSubView === 'media'
              ? 'bg-[#e840f3]/15 text-[#e840f3] border border-[#e840f3]/25 shadow-[0_0_8px_rgba(232,64,243,0.1)]'
              : 'text-slate-450 hover:text-slate-205'
          }`}
        >
          Media Generation
        </button>
        <button
          onClick={() => setActiveSubView('ad')}
          className={`flex-1 py-2 px-3 rounded-xl font-mono text-[10px] font-black uppercase text-center transition-all cursor-pointer ${
            activeSubView === 'ad'
              ? 'bg-cyan-950/40 text-cyan-400 border border-cyan-500/20'
              : 'text-slate-450 hover:text-slate-205'
          }`}
        >
          AI Ad Factory / UGC
        </button>
        <button
          onClick={() => setActiveSubView('social')}
          className={`flex-1 py-2 px-3 rounded-xl font-mono text-[10px] font-black uppercase text-center transition-all cursor-pointer ${
            activeSubView === 'social'
              ? 'bg-purple-950/30 text-purple-300 border border-purple-500/20'
              : 'text-slate-450 hover:text-slate-205'
          }`}
        >
          Social Scheduler
        </button>
      </div>

      <AnimatePresence mode="wait">
        {/* VIEW 1: MEDIA STUDIO */}
        {activeSubView === 'media' && (
          <motion.div
            key="media-studio"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            <div className="lg:col-span-2 space-y-6">
              {/* Form Prompt */}
              <div className="glass-panel border border-[#e840f3]/15 p-5 rounded-2xl bg-black/40">
                <form onSubmit={handleGenerateMedia} className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xs font-mono font-black text-white uppercase tracking-wider flex items-center gap-1.5">
                      <ImageIcon className="w-4 h-4 text-[#e840f3]" />
                      Render Visual Assets
                    </h3>
                    <span className="text-[10px] text-pink-400 font-mono">
                      Renders Remaining: {user.rendersRemaining} Credits
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setRenderType('Image')}
                      className={`py-1.5 px-3 rounded font-mono text-[9px] uppercase font-black cursor-pointer border ${
                        renderType === 'Image' ? 'bg-[#e840f3]/15 border-[#e840f3]/30 text-white' : 'border-zinc-800 text-slate-500'
                      }`}
                    >
                      Image Render
                    </button>
                    <button
                      type="button"
                      onClick={() => setRenderType('Video')}
                      className={`py-1.5 px-3 rounded font-mono text-[9px] uppercase font-black cursor-pointer border ${
                        renderType === 'Video' ? 'bg-[#e840f3]/15 border-[#e840f3]/30 text-white' : 'border-zinc-800 text-slate-500'
                      }`}
                    >
                      Video Engine-Render
                    </button>
                  </div>

                  <div className="relative">
                    <textarea
                      placeholder={`Describe the high-fidelity ${renderType.toLowerCase()} concept prompts and styles...`}
                      value={mediaPrompt}
                      onChange={(e) => setMediaPrompt(e.target.value)}
                      rows={3}
                      className="w-full bg-black/50 border border-zinc-850 rounded-xl p-3.5 text-xs font-mono text-white tracking-widest outline-none focus:border-[#e840f3]/45 uppercase"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isRendering}
                    className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-[#e840f3] text-[#000] font-mono text-xs font-black uppercase tracking-wider rounded-xl hover:opacity-90 active:scale-97 cursor-pointer transition-all flex items-center justify-center gap-2"
                  >
                    {isRendering ? 'RENDERING IN GPU CLUSTER...' : 'INITIALIZE GENERATION'}
                  </button>
                </form>
              </div>

              {/* Renders Queue Gallery */}
              <div className="space-y-4">
                <h3 className="text-xs font-mono font-black text-slate-400 uppercase tracking-widest pl-1">
                  Active Asset Export Library
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {rendersList.map((rnd) => (
                    <div key={rnd.id} className="glass-panel border border-zinc-850 p-4 rounded-xl bg-black/40 space-y-3">
                      <div className="flex justify-between text-[8px] font-mono border-b border-zinc-900 pb-1.5">
                        <span className="text-pink-400 font-extrabold">{rnd.type} EXPORT</span>
                        <span className="text-emerald-400 flex items-center gap-1">
                          <Check size={8} /> {rnd.status}
                        </span>
                      </div>

                      {rnd.url ? (
                        <div className="w-full aspect-video rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800 relative">
                          <img
                            src={rnd.url}
                            alt={rnd.prompt}
                            referrerPolicy="no-referrer"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-full h-24 rounded-lg bg-zinc-950/80 border border-zinc-900 flex flex-col items-center justify-center text-slate-500 font-mono text-[9px]">
                          <Video size={16} className="text-slate-450 animate-pulse mb-1" />
                          <span>RENDERED MP4 STREAM PATH</span>
                        </div>
                      )}

                      <p className="text-[10px] font-sans text-slate-350 leading-tight border-b border-zinc-900/40 pb-2 truncate uppercase font-medium">
                        "{rnd.prompt}"
                      </p>

                      <div className="flex justify-between items-center text-[10px] font-mono">
                        <button
                          onClick={() => {
                            setNewPostCaption(`Check out our fresh AI render results! #${rnd.prompt.split(' ').slice(0,2).join('')}`);
                            setNewPostPlatform('LinkedIn');
                            setActiveSubView('social');
                            onAddLog('Media Studio', 'Import render into Social scheduler', 'SUCCESS', 'Render assets drafted successfully.');
                          }}
                          className="text-cyan-400 uppercase hover:underline text-[9px]"
                        >
                          Send to Social Post
                        </button>
                        <a
                          href={rnd.url || '#'}
                          download={`render_${rnd.id}`}
                          className="text-slate-500 hover:text-white text-[9px] uppercase"
                        >
                          Download Raw
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Studio Metrics */}
            <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 text-xs font-mono space-y-4">
              <h3 className="text-xs font-sans font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-2">
                GPU cluster core load
              </h3>
              <div className="space-y-3 text-[10px]">
                <div className="flex justify-between">
                  <span className="text-slate-500">NODE RENDERING:</span>
                  <span className="text-white">Active</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">ACTIVE GPU INSTANCE:</span>
                  <span className="text-emerald-400">AMD EPYC + Nvidia H100</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">RESOLUTION RATING:</span>
                  <span className="text-[#00ffff]">4k ULTRA UHD EXPORTS</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 2: AD FACTORY & UGC */}
        {activeSubView === 'ad' && (
          <motion.div
            key="ad-factory"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            <div className="lg:col-span-2 space-y-6">
              {/* Ad Factory Generator Input */}
              <div className="glass-panel border border-cyan-500/20 p-5 rounded-2xl bg-black/40 space-y-4">
                <h3 className="text-xs font-mono font-black text-white uppercase tracking-wider flex items-center gap-1.5">
                  <Megaphone className="w-4 h-4 text-cyan-400" />
                  Generate High-Converting Script Structures
                </h3>

                <div className="space-y-1.5">
                  <label className="text-[9.5px] font-mono text-slate-500 font-extrabold uppercase">PRODUCT NAME OR CAMPAIGN OBJECTIVE:</label>
                  <input
                    type="text"
                    value={adSubject}
                    onChange={(e) => setAdSubject(e.target.value)}
                    className="w-full bg-black/50 border border-zinc-800 rounded-lg px-3 py-2 text-xs font-mono text-white tracking-wider outline-none focus:border-cyan-400/50 uppercase"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleGenerateAdScript}
                  disabled={isGeneratingAd}
                  className="w-full py-2.5 bg-cyan-950/80 hover:bg-[#00ffff] hover:text-[#000] border border-cyan-500/20 hover:border-cyan-400 text-cyan-400 rounded-xl font-mono text-xs font-black uppercase tracking-widest cursor-pointer transition-colors"
                >
                  {isGeneratingAd ? 'CALCULATING AD REELS COPYS...' : 'GENERATE SCRIPT'}
                </button>
              </div>

              {/* Generated Scripts presentation panel */}
              {generatedScript && (
                <div className="glass-panel border border-cyan-555/20 p-5 rounded-2xl bg-gradient-to-r from-cyan-950/5 via-black/45 to-zinc-950/20 space-y-4">
                  <div className="flex justify-between border-b border-zinc-900 pb-2">
                    <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider">
                      UGC-Style Content Script Drafts
                    </h4>
                    <button
                      onClick={() => {
                        onAddLog('Ad Factory', 'Copy Script to clipboard', 'SUCCESS', 'Script clipboard serialization complete.');
                        alert('Script copied to clipboard!');
                      }}
                      className="text-cyan-400 text-[10px] font-mono uppercase hover:underline"
                    >
                      Copy Draft
                    </button>
                  </div>

                  <div className="space-y-4 text-xs font-sans leading-relaxed text-slate-300 normal-case">
                    <div className="border-l-2 border-[#e840f3] pl-3 space-y-1">
                      <div className="text-[10px] font-mono font-black text-[#e840f3] uppercase">Ad Hook variation:</div>
                      <p className="font-sans font-medium">{generatedScript.hook}</p>
                    </div>

                    <div className="border-l-2 border-cyan-400 pl-3 space-y-1">
                      <div className="text-[10px] font-mono font-black text-cyan-400 uppercase">Interactive Body copy:</div>
                      <p className="font-sans font-medium">{generatedScript.body}</p>
                    </div>

                    <div className="border-l-2 border-purple-400 pl-3 space-y-1">
                      <div className="text-[10px] font-mono font-black text-purple-400 uppercase">Strong CTA:</div>
                      <p className="font-sans font-medium">{generatedScript.cta}</p>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-zinc-900/60 font-mono text-[9px] text-[#00ffff] cursor-pointer"
                    onClick={() => {
                      setNewPostCaption(`${generatedScript.hook.replace(/HOOK.*: "/,'').replace(/"$/,'')} @NexusOS`);
                      setActiveSubView('social');
                      onAddLog('Ad Factory', 'Import script to post scheduler', 'SUCCESS', 'Ad scripts converted to post scheduled draft.');
                    }}
                  >
                    🚀 Trigger Post directly with this hook
                  </div>
                </div>
              )}
            </div>

            {/* Ad statistics / logs */}
            <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 space-y-3">
              <h4 className="text-[10px] font-mono font-black text-slate-400 uppercase tracking-widest pl-1">
                Automation Pipelines Links
              </h4>
              <div className="text-[10.5px] font-sans normal-case text-slate-400 leading-relaxed space-y-2">
                <p>1. Searching for high-potential keywords triggers the Campaign Agent.</p>
                <p>2. The script compiles via our AI Ad Factory engines.</p>
                <p>3. Rendering matches are placed inside social media scheduler boards immediately.</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 3: SOCIAL MEDIA AUTOMATION */}
        {activeSubView === 'social' && (
          <motion.div
            key="social-scheduler"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {/* Post creator (2/3 col) */}
            <div className="lg:col-span-2 space-y-6">
              <div className="glass-panel border border-purple-500/15 p-5 rounded-2xl bg-black/40">
                <form onSubmit={handleCreateSocialPost} className="space-y-4">
                  <h3 className="text-xs font-mono font-black text-white uppercase tracking-wider flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-purple-400" />
                    Coordinate Content Publishing Queue
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-1">
                    <div className="space-y-1">
                      <label className="text-[9px] font-mono text-slate-500 uppercase">Target Platform:</label>
                      <select
                        value={newPostPlatform}
                        onChange={(e: any) => setNewPostPlatform(e.target.value)}
                        className="w-full bg-[#050510] border border-zinc-800 rounded-lg px-2 py-2 text-xs font-mono text-cyan-400 outline-none"
                      >
                        <option value="LinkedIn">👔 LinkedIn Business</option>
                        <option value="Twitter">🐦 Twitter / X Matrix</option>
                        <option value="YouTube">🎥 YouTube Broadcasts</option>
                        <option value="Facebook">👥 Facebook Page</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[9px] font-mono text-slate-500 uppercase">Post Caption:</label>
                    <textarea
                      placeholder="Write hashtags, copy writing, and references to add..."
                      value={newPostCaption}
                      onChange={(e) => setNewPostCaption(e.target.value)}
                      rows={3}
                      className="w-full bg-black/50 border border-zinc-850 rounded-xl p-3.5 text-xs font-mono text-white tracking-widest outline-none focus:border-purple-450 uppercase"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-2.5 bg-gradient-to-r from-purple-600/40 to-cyan-500/40 hover:from-purple-600/65 hover:to-cyan-550/65 text-white border border-purple-400/30 rounded-xl font-mono text-xs font-black uppercase tracking-wider cursor-pointer"
                  >
                    Schedule Content Block
                  </button>
                </form>
              </div>

              {/* Scheduled queue */}
              <div className="space-y-4">
                <h3 className="text-xs font-mono font-black text-slate-400 uppercase tracking-widest pl-1">
                  Scheduler Publishing Queue
                </h3>

                {posts.length === 0 ? (
                  <div className="text-center py-10 bg-black/20 border border-zinc-900 rounded-2xl text-xs font-mono text-slate-500 uppercase">
                    No posts scheduled in the content array.
                  </div>
                ) : (
                  posts.map((post) => (
                    <div key={post.id} className="glass-panel border border-zinc-950 p-4 rounded-xl bg-black/30 flex items-start justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex gap-2 items-center">
                          <span className="text-[9px] font-mono text-[#00ffff] uppercase font-black bg-cyan-950/40 border border-cyan-500/10 px-2 py-0.5 rounded">
                            {post.platform}
                          </span>
                          <span className={`text-[8.5px] font-mono ${
                            post.status === 'Approved' ? 'text-emerald-400' : 'text-amber-500'
                          } font-extrabold uppercase`}>
                            {post.status}
                          </span>
                        </div>

                        <p className="text-xs font-sans text-slate-300 leading-relaxed uppercase pr-4 font-medium">
                          {post.caption}
                        </p>

                        <div className="text-[8.5px] font-mono text-slate-500 uppercase">
                          Scheduled: <strong className="text-slate-400">{post.scheduledTime || 'Trigger queue'}</strong>
                        </div>
                      </div>

                      <div className="flex gap-1.5 shrink-0">
                        {post.status !== 'Approved' && (
                          <button
                            onClick={() => handleApprovePost(post.id)}
                            className="py-1 px-2.5 bg-emerald-950/40 hover:bg-emerald-950 text-emerald-400 border border-emerald-500/20 rounded font-mono text-[8px] uppercase font-black cursor-pointer"
                          >
                            Approve
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePost(post.id)}
                          className="py-1 px-1.5 bg-rose-955/20 hover:bg-rose-950 text-rose-400 rounded cursor-pointer"
                        >
                          ✖
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Performance analysis charts/numbers widget */}
            <div className="space-y-6">
              <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 space-y-4 text-xs font-mono">
                <h3 className="text-xs font-sans font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-2 flex items-center gap-1.5">
                  <BarChart2 className="w-4 h-4 text-purple-400" />
                  Social Performance Analytics
                </h3>
                <div className="space-y-3 text-[10px]">
                  <div className="flex justify-between">
                    <span className="text-slate-500">ENGAGEMENT RATE:</span>
                    <span className="text-emerald-400 font-bold">+18.4% (Over benchmark)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">TOTAL REACH EXPORTS:</span>
                    <span className="text-white">44,500 impressions</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">UGC CLIPS CONVERSIONS:</span>
                    <span className="text-cyan-400 font-black">2.4% Click-Through</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
